<template>
  <v-sheet class="mapTaskGauge aid-bg-grey aid-opacity-07">
    <v-radio-group
      v-model="selectedGaugeMode"
      @change="updateGaugeOptions()"
      column
      hide-details
      class=" ma-0 py-2 pl-2"
    >
      <v-radio
        class="caption mb-0"
        color="primary"
        v-for="gaugeModeItem in gaugeModeItems"
        :key="gaugeModeItem.text"
        :value="gaugeModeItem.value"
      >
        <template v-slot:label>
          <v-tooltip right>
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on" class="caption">{{ gaugeModeItem.text }}</div>
            </template>
            <span>{{ gaugeModeItem.description }}</span>
          </v-tooltip>
        </template>
      </v-radio>
    </v-radio-group>
    <div v-if="showGauge()">
      <e-chart :options="gaugeOptions" autoresize :theme="theme" />
    </div>
  </v-sheet>
</template>

<script>
import * as SystemOfUnits from '@/lib/system-of-units'
import * as SystemOfUnitsConstants from '@/lib/system-of-units-constants'
import MapCustomControlMixin from '@/components/mixins/map-custom-control-mixin'
import MapMixin from '@/components/mixins/map-mixin'
import MapGaugeMixin from '@/components/mixins/map-gauge-mixin'
import * as Constants from '@/lib/constants'
import i18n from '@/lib/i18n'
import 'echarts/lib/chart/bar'
import 'echarts/lib/component/axisPointer'
import 'echarts/lib/component/title'
import 'echarts/lib/component/tooltip'
import ECharts from 'vue-echarts'

const convertUnits = require('convert-units')

export default {
  name: 'MapTaskGaugeComponent',
  mixins: [MapCustomControlMixin, MapMixin, MapGaugeMixin],
  components: { 'e-chart': ECharts },
  props: { theme: { type: String } },

  methods: {
    updateGaugeOptions: function() {
      this.$emit('change', this.selectedGaugeMode)

      if (this.hasFreeFunctionality()) {
        return
      }

      let opts = {
        grid: { left: '2%', right: '2%', top: 10, bottom: 0, containLabel: true },
        xAxis: { type: 'category', data: [''], show: false },
        yAxis: { type: 'value', axisLabel: { formatter: '{value}' } },
      }

      const stageCount = Constants.TASK_DIVIDED_LEVEL_COUNT
      let minValue = 0
      let maxValue = 0
      if (this.selectedGaugeMode === Constants.MAP_TASK_COLORED_MODE_ALTITUDE) {
        minValue = this.$store.getters.currentTaskMinAltitude
        maxValue = this.$store.getters.currentTaskMaxAltitude
        let targetUnit = ''
        if (SystemOfUnits.isMetric()) {
          targetUnit = 'm'
        } else {
          targetUnit = 'ft'
          minValue = convertUnits(minValue)
            .from('m')
            .to('ft')
          maxValue = convertUnits(maxValue)
            .from('m')
            .to('ft')
        }
        opts.yAxis.axisLabel.formatter = '{value}' + targetUnit
      } else if (this.selectedGaugeMode === Constants.MAP_TASK_COLORED_MODE_SPEED) {
        minValue = this.$store.getters.currentTaskMinSpeed
        maxValue = this.$store.getters.currentTaskMaxSpeed
        let targetUnit = ''
        if (SystemOfUnits.isMetric()) {
          targetUnit = 'km/h'
          minValue = convertUnits(minValue)
            .from(SystemOfUnitsConstants.MeterPerSecond)
            .to(SystemOfUnitsConstants.KilloMeterPerHour)
          maxValue = convertUnits(maxValue)
            .from(SystemOfUnitsConstants.MeterPerSecond)
            .to(SystemOfUnitsConstants.KilloMeterPerHour)
        } else {
          targetUnit = 'mph'
          minValue = convertUnits(minValue)
            .from('km')
            .to('mi')
          maxValue = convertUnits(maxValue)
            .from('km')
            .to('mi')
        }
        opts.yAxis.axisLabel.formatter = '{value}' + targetUnit
      } else if (this.selectedGaugeMode === Constants.MAP_TASK_COLORED_MODE_GNSS_NUM_OF_SATELLITES) {
        minValue = this.$store.getters.currentTaskMinGnssNumOfSatellites
        maxValue = this.$store.getters.currentTaskMaxGnssNumOfSatellites
        opts.yAxis.axisLabel.formatter = function(value) {
          return Math.round(value)
        }
      } else if (this.selectedGaugeMode === Constants.MAP_TASK_COLORED_MODE_GNSS_STATUS) {
        minValue = this.$store.getters.currentTaskMinGnssStatus
        maxValue = this.$store.getters.currentTaskMaxGnssStatus
        opts.yAxis.axisLabel.formatter = function(value) {
          return Math.round(value)
        }
      } else if (this.selectedGaugeMode === Constants.MAP_TASK_COLORED_MODE_GUIDANCE_ERROR) {
        minValue = this.$store.getters.currentTaskMinGuidanceErrors
        maxValue = this.$store.getters.currentTaskMaxGuidanceErrors
        opts.yAxis.axisLabel.formatter = '{value} m'
      } else if (this.selectedGaugeMode === Constants.MAP_TASK_COLORED_MODE_GUIDANCE_HEADING_ERROR) {
        minValue = this.$store.getters.currentTaskMinGuidanceHeadingErrors
        maxValue = this.$store.getters.currentTaskMaxGuidanceHeadingErrors
        opts.yAxis.axisLabel.formatter = '{value} deg'
      } else if (this.selectedGaugeMode === Constants.MAP_TASK_COLORED_MODE_SLANT) {
        minValue = this.$store.getters.currentTaskMinSlant
        maxValue = this.$store.getters.currentTaskMaxSlant
        opts.yAxis.axisLabel.formatter = '{value} deg'
      }

      opts = this.createMapGaugeOption(opts, minValue, maxValue, stageCount)

      this.gaugeOptions = opts
    },

    showGauge: function() {
      if (this.hasFreeFunctionality()) {
        return false
      }
      if (this.selectedGaugeMode === Constants.MAP_TASK_COLORED_MODE_NONE) {
        return false
      }
      if (this.selectedGaugeMode === Constants.MAP_TASK_COLORED_MODE_COLOR_CODED) {
        return false
      }
      return true
    },
  },

  data() {
    return {
      gaugeModeItems: [
        {
          text: i18n.tt('customNone'),
          value: Constants.MAP_TASK_COLORED_MODE_NONE,
          description: i18n.tt('customMapGaugeDescriptionNone'),
        },
        {
          text: i18n.tt('customColorCodeFill'),
          value: Constants.MAP_TASK_COLORED_MODE_COLOR_CODED,
          description: i18n.tt('customMapGaugeDescriptionColorCodedFill'),
        },
        {
          text: i18n.tt('customHeight'),
          value: Constants.MAP_TASK_COLORED_MODE_ALTITUDE,
          description: i18n.tt('customMapGaugeDescriptionHeight'),
        },
        {
          text: i18n.tt('customSpeed'),
          value: Constants.MAP_TASK_COLORED_MODE_SPEED,
          description: i18n.tt('customMapGaugeDescriptionSpeed'),
        },
        {
          text: i18n.tt('customNumOfSatellites'),
          value: Constants.MAP_TASK_COLORED_MODE_GNSS_NUM_OF_SATELLITES,
          description: i18n.tt('customMapGaugeDescriptionNumOfSatellites'),
        },
        {
          text: i18n.tt('customGnssStatus'),
          value: Constants.MAP_TASK_COLORED_MODE_GNSS_STATUS,
          description: i18n.tt('customMapGaugeDescriptionGnssStatus'),
        },
        {
          text: i18n.tt('customLeftRightMisalignment'),
          value: Constants.MAP_TASK_COLORED_MODE_GUIDANCE_ERROR,
          description: i18n.tt('customMapGaugeDescriptionLeftRightMisalignment'),
        },
        {
          text: i18n.tt('customAzimuthShift'),
          value: Constants.MAP_TASK_COLORED_MODE_GUIDANCE_HEADING_ERROR,
          description: i18n.tt('customMapGaugeDescriptionAzimuthShift'),
        },
        {
          text: i18n.tt('customGradient'),
          value: Constants.MAP_TASK_COLORED_MODE_SLANT,
          description: i18n.tt('customMapGaugeDescriptionGradient'),
        },
      ],
      gaugeOptions: undefined,
      selectedGaugeMode: Constants.MAP_TASK_COLORED_MODE_NONE,
      popup: false,
    }
  },
}
</script>

<style scoped>
.mapTaskGauge {
  position: absolute;
  top: 18px;
  left: 18px;
  z-index: 2;
}
.v-label {
  font-size: 1.2rem !important;
}
.echarts {
  width: 100%;
  height: 120px;
  margin-bottom: 8px;
}
.popup-card {
  padding: 10px 10px 10px 10px;
}
</style>
